import Modal from 'components/modal/basic-modal'
import folderService from 'data/folder'
import { formatDate } from 'data/lib/dates.ts'
import { local } from 'data/lib/local-json'
// lib
import client from 'data/_client'
import _ from 'lodash'
import React, { useEffect, useState } from 'react'
// styles
import { ButtonWithPress_s, H4, Label_s } from 'styles/app-styles'
import { Dropdown_s } from 'styles/input-styles'
import {
  ReportContainerStandalone_s,
  ReportForms_s,
  ReportForm_s, ReportSelectionItem_s, ReportSelection_s
} from 'styles/report-styles'
// components
import { CommercialCashFlow, propertyFilter as CCFPFilter } from './cash-flow'
import { CrossAssetCashFlow } from './cross-asset-cash-flow'
import { Expiration, propertyFilter as ExpirationPFilter } from './expiration'
import FolderSettings from './folder-settings'
import { HospitalityCashFlow, propertyFilter as HCFFilter } from './hospitality-cash-flow'
import { MarketLeaseValues, propertyFilter as MarketLeaseValuesPFilter } from './market-lease-values'
import { MultifamilyCashFlow, propertyFilter as MCFPFilter } from './multifamily-cash-flow'
import { Occupancy, propertyFilter as OccupancyFilter } from './occupancy'
import PropertyInformation from './property-information'
import PropertyMetrics from './property-metrics'
import ReportPortfolioGrid from './report-portfolio-grid'
import ReportPropertyGrid, { ReportGridProperty } from './report-property-grid-standalone'
import { SelectionGrid_s, SelectionMethod_s } from './report-styles'
import { propertyFilter as TenantRosterPFilter, sortOptions as TenantRosterSortOptions, TenantRoster } from './tenant-roster'
import ValuationSummary from './valuation-summary'
import { propertyFilter as WeightedAverageLeaseTermFilter, WeightedAverageLeaseTerm } from './weighted-average-lease-term'


// Types report components must obey

type ReportFormParameters = {
  report: string // can be overriden in subtypes for local correctness
  // selection_method: 'current_folder' | 'subfolder' | 'portfolio'
}

// Types for top-level Reports component
type Folderish = {
  term: number
  reportbegindate: string
}

const SortHeader = ({sort, setSort, sortOptions}) => (
  <div>
    <Label_s>Sort Order</Label_s>
    <Dropdown_s
      onChange={(e) => setSort(e.value)}
      optionLabel="value"
      options={sortOptions}
      value={sort}
    />
  </div>
)

type ReportProps = {
  isBeta?: boolean
  runButtonText: string
  submit: Function
  parameters: Function
  propertyFilter?: (property: { type: string }) => boolean
}

const ReportsStandalone = (props: ReportProps) => {
  const [activeReport, setReport] = useState<number>(0)
  const [folder, setFolder] = useState<Folderish>({
    term: 0,
    reportbegindate: ''
  })
  const [lineItemDisplay, setLineItemDisplay] = useState<string[]>([])
  const [parameters, setParameters] = useState<ReportFormParameters>()
  const [message, setMessage] = useState<string>('')
  const [valid, setValid] = useState<boolean>(false)
  const [formvalid, setFormValid] = useState<boolean>(false)
  const [selected, setSelected] = useState<ReportGridProperty[]>([])
  const [selectedPortfolios, setSelectedPortfolios] = useState<object[]>([])
  const [selectionMethod, setSelectionMethod] = useState<string>('')
  const [selectionMethodOptions, setSelectionMethodOptions] = useState<string[]|null>(null)

  useEffect(() => {
    const currentFolder = _.last(local('folderPath'))
    const folderSubscription = folderService.stream().subscribe((data) => {
      if (data.state == 'ok') {
        const folders = data.value
        const folder = _.find(folders, { folderid: currentFolder })
        if (!folder) return
        setFolder({
          term: folder.term || 10,
          reportbegindate: folder.reportbegindate || formatDate(new Date())
        })
        // Line Item Display logic
        let parentFolder = _.find(folders, {folderid: folder.folderid}).parentfolder
        let isAssetManagementFolder = folder['code'] === '*AssetManagement'
        while (parentFolder) {
          const parent = _.find(folders, {folderid: parentFolder})
          if (parent && parent['code'] === '*AssetManagement') isAssetManagementFolder = true
          if (_.find(folders, {folderid: parent?.parentfolder})) parentFolder = _.find(folders, {folderid: parent?.parentfolder}).folderid
          else break
        }
        let selectionMethodOptions:string[] = ['Current Folder']
        if (isAssetManagementFolder){
          setLineItemDisplay(['Detail Account #', 'Summary Account #', 'Cash Flow Grouping', 'As Entered'])
          selectionMethodOptions.unshift('Portfolios')
        } else {
          setLineItemDisplay(['Cash Flow Grouping', 'As Entered'])
        }
        setSelectionMethodOptions(selectionMethodOptions);
      }
    })
    currentFolder && folderService.fetchAllFolders()
    return folderSubscription.unsubscribe.bind(folderSubscription)
  }, [])

  useEffect(() => {
    let _selectionMethodOptions = _.cloneDeep(selectionMethodOptions) || null;
    if (_selectionMethodOptions) _selectionMethodOptions.push('Current Folder and Subfolders')

    let _selectionMethod = _selectionMethodOptions
      ? _selectionMethodOptions && _selectionMethodOptions.includes(selectionMethod)
        ? selectionMethod
        : _selectionMethodOptions[0]
      : null

    if (_selectionMethod && _selectionMethod !== "Portfolios"){
      if (checkSelectedProperties(selected)) setValid(false)
      else setValid(formvalid && selected.length > 0)
    } else if (_selectionMethod) {
      setValid(formvalid && selectedPortfolios.length > 0)
    }

    if (!_.isEmpty(selectedPortfolios) && (_.isNil(selectionMethod) || selectionMethod === "")){
      setSelectionMethod("Portfolios")
    }
  }, [formvalid, selected, selectedPortfolios, selectionMethod, selectionMethodOptions])

  const checkSelectedProperties = (properties:object[]):boolean => {
    const hasUncalculated = !!_.find(
      properties,
      (p) => !['Calculated', 'Calculated with Warnings'].includes(p.status)
    )
    const hasDisallowed = false
    return (hasUncalculated || hasDisallowed)
  }

  const hasUncalculated = (properties:object[]):boolean => {
    const uncalculated = !!_.find(
      properties,
      (p) => !['Calculated', 'Calculated with Warnings'].includes(p.status)
    )
    return uncalculated
  }

  const makeReportBody = () => {
    return {
      folderid: _.last(local('folderPath')),
      propertyid: _.map(selected, 'id'),
      portfolioid: _.map(selectedPortfolios, 'portfolioid'),
      output: {
        file_type: 'xlsx',
        timezone_offset: new Date().getTimezoneOffset()
      },
      errors: [],
      hasUncalculated: hasUncalculated(selected),
      use_auth: true
    }
  }

  const getSelectionMethod = () => {
    switch(selectionMethod){
      case 'Current Folder and Subfolders': return 'subfolder'
      case 'Portfolios':                    return 'portfolio'
      case 'Current Folder':
      default:                              return 'current_folder'
    }
  }

  const sortOptions = false
  const [sort, setSort] = useState<string>('')
  useEffect(() => {
    setSort(_.get(sortOptions, '0.value', ''))
  }, [sortOptions])

  let _selectionMethodOptions = _.cloneDeep(selectionMethodOptions) || null;
  if (_selectionMethodOptions) _selectionMethodOptions.push('Current Folder and Subfolders')
  let _selectionMethod = _selectionMethodOptions ? _selectionMethodOptions && _selectionMethodOptions.includes(selectionMethod) ? selectionMethod : _selectionMethodOptions[0] : null
  if (_selectionMethodOptions) _selectionMethodOptions = _selectionMethodOptions.map(option => { return { 'label': option, 'value': option }})

  return (
    <ReportContainerStandalone_s>
      {props.isBeta && (<label style={{ color: "red" }}>This feature is currently in beta.  Please contact Support for more information.</label>)}
      <ReportForms_s className="slim-scroll slim-scroll-hover">
        <div>
          <ButtonWithPress_s primary onClick={() => props.submit(makeReportBody())}>
            {props.runButtonText}
          </ButtonWithPress_s>
        </div>
        <ReportForm_s>
          {sortOptions && <SortHeader sort={sort} setSort={setSort} sortOptions={sortOptions}/>}
          { <props.parameters /> }
          { _selectionMethodOptions && <SelectionGrid_s>
            <SelectionMethod_s>
              <Label_s>Selection Method</Label_s>
              <Dropdown_s
                onChange={(e) => setSelectionMethod(e.value)}
                options={_selectionMethodOptions}
                value={_selectionMethod}
                style={{width: '230px'}}
              />
            </SelectionMethod_s>
            { ((_selectionMethod.includes('Current Folder') || !_selectionMethod && _selectionMethodOptions[0].value === 'Current Folder'))
              && <ReportPropertyGrid
                    propertyFilter={props.propertyFilter}
                    onChange={setSelected}
                    selectionMethod={selectionMethod}
                    activeReport={activeReport}
                  />
            }
            { (_selectionMethod === 'Portfolios' || !_selectionMethod && _selectionMethodOptions[0].value === 'Portfolios')
              && <ReportPortfolioGrid onChange={setSelectedPortfolios} />
            }
          </SelectionGrid_s> }
        </ReportForm_s>
      </ReportForms_s>
      {!!message && (
        <Modal show={!!message} title="Report Error" toggleShow={() => setMessage('')}>
          {message}
        </Modal>
      )}
    </ReportContainerStandalone_s>
  )
}

export default ReportsStandalone
